import { ReactComponent as OrigMinerView } from "@assets/redesign/icons/minerScheme.svg";
import { ReactComponent as WlpMinerView } from "@components/WLP202603/assets/redesign/icons/minerScheme.svg";
import { Input } from "@components/global/new/Input/Input";
import { IMinerProfileEditorStore } from "@mobx/store/minerProfiles/editor";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import { WLP202603 } from "../../../../../config";
import styles from "../MinerViewBlock/MinerViewBlock.module.scss";

const MinerView = WLP202603 ? WlpMinerView : OrigMinerView;

interface MobxMinerViewBlockProps {
  readonly editorStore: IMinerProfileEditorStore;
  readonly disabled?: boolean;
  checkErrors?(name: string): boolean;
}

function MobxMinerViewBlock({
  editorStore,
  disabled,
  checkErrors,
}: MobxMinerViewBlockProps) {
  const { formatMessage } = useIntl();
  const draft = editorStore.draft;

  const makePlaceholder = (id: string) =>
    `${formatMessage({ id })}, ${formatMessage(
      { id: "manage.profile.params.unit" },
      { value: "", unit: "мм" }
    )}`;

  const heightPlaceholder = makePlaceholder("manage.profile.height");
  const widthPlaceholder = makePlaceholder("manage.profile.width");
  const depthPlaceholder = makePlaceholder("manage.profile.depth");

  const volume = useMemo(() => {
    const width = Number(draft?.width) || 0;
    const height = Number(draft?.height) || 0;
    const depth = Number(draft?.depth) || 0;

    if (!width || !height || !depth) return 0;
    return width * height * depth;
  }, [draft?.width, draft?.height, draft?.depth]);

  const volumeValue = volume ? Number((volume / 1000000000).toFixed(8)) : 0;
  const volumeUnit = volume ? "м" : "";

  if (!draft) return null;

  return (
    <div className={styles.miner_view}>
      <MinerView />
      <div className={styles.params}>
        <span className={styles.volume}>
          {formatMessage({ id: "manage.profile.volume" })}:{" "}
          {formatMessage(
            { id: "manage.profile.params.unit" },
            { value: volumeValue, unit: volumeUnit }
          )}
          <sup>3</sup>
        </span>
        <Input
          type="number"
          size="small"
          label={heightPlaceholder}
          placeholder={heightPlaceholder}
          outlined={!disabled && !checkErrors?.("height")}
          readOnly={disabled}
          value={draft.height}
          onChange={(e) =>
            editorStore.setDimensions({
              height: e.currentTarget.value,
            })
          }
          status={(checkErrors?.("height") && "error") || ""}
          autoComplete="off"
          id="height"
        />
        <Input
          type="number"
          size="small"
          label={widthPlaceholder}
          placeholder={widthPlaceholder}
          outlined={!disabled && !checkErrors?.("width")}
          readOnly={disabled}
          value={draft.width}
          onChange={(e) =>
            editorStore.setDimensions({
              width: e.currentTarget.value,
            })
          }
          status={(checkErrors?.("width") && "error") || ""}
          autoComplete="off"
          id="width"
        />
        <Input
          type="number"
          size="small"
          label={depthPlaceholder}
          placeholder={depthPlaceholder}
          outlined={!disabled && !checkErrors?.("depth")}
          readOnly={disabled}
          value={draft.depth}
          onChange={(e) =>
            editorStore.setDimensions({
              depth: e.currentTarget.value,
            })
          }
          status={(checkErrors?.("depth") && "error") || ""}
          autoComplete="off"
          id="depth"
        />
      </div>
    </div>
  );
}

export default observer(MobxMinerViewBlock);
